import { useEffect, useRef, useState } from "react";

import type { ConversationSession } from "@/lib/conversation-session";

export function useAudioRecording(session: ConversationSession) {
	const [phase, setPhase] = useState<"requesting" | "recording" | "stopping" | null>(null);
	const [seconds, setSeconds] = useState(0);
	const [error, setError] = useState(false);
	const request = useRef(0);
	const recording = useRef<{
		recorder: MediaRecorder;
		stream: MediaStream;
		startedAt: number;
		discard: boolean;
	} | null>(null);

	const stop = () => {
		request.current += 1;

		if (recording.current?.recorder.state === "recording") {
			recording.current.recorder.stop();
			setPhase("stopping");
		} else {
			setPhase(null);
		}

		for (const track of recording.current?.stream.getTracks() || []) {
			track.stop();
		}
	};

	const discard = () => {
		if (recording.current) {
			recording.current.discard = true;
		}

		stop();
		session.removeAudio();
	};

	const start = async () => {
		if (phase || session.state.audio || session.state.sending) {
			return;
		}

		const attempt = ++request.current;
		setError(false);
		setPhase("requesting");
		const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

		if (request.current !== attempt) {
			for (const track of stream.getTracks()) {
				track.stop();
			}

			return;
		}

		const mimeType = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/mp4"].find((mime) =>
			MediaRecorder.isTypeSupported(mime),
		);
		let recorder: MediaRecorder;

		try {
			recorder = new MediaRecorder(stream, { mimeType });
		} catch (error) {
			for (const track of stream.getTracks()) {
				track.stop();
			}

			throw error;
		}
		const capture = { recorder, stream, startedAt: Date.now(), discard: false };
		const chunks: Blob[] = [];
		let bytes = 0;

		recording.current = capture;
		recorder.ondataavailable = (event) => {
			if (event.data.size > 0) {
				chunks.push(event.data);
				bytes += event.data.size;
			}

			if (bytes >= 15 * 1024 * 1024 && recorder.state === "recording") {
				stop();
			}
		};
		recorder.onstop = () => {
			for (const track of stream.getTracks()) {
				track.stop();
			}

			if (!capture.discard && bytes > 0) {
				const file = new File(chunks, "gravacao", { type: recorder.mimeType });

				session.setAudio({
					id: crypto.randomUUID(),
					file,
					url: URL.createObjectURL(file),
					duration: Math.max(1, Math.round((Date.now() - capture.startedAt) / 1000)),
					sendAvailableAt: Date.now() + 1000,
				});
			}

			recording.current = null;
			setPhase(null);
		};
		recorder.addEventListener("error", () => {
			setError(true);
			stop();
		});
		recorder.start(1000);
		setSeconds(0);
		setPhase("recording");
	};

	useEffect(() => {
		if (phase !== "recording") {
			return;
		}

		const timer = setInterval(() => {
			if (recording.current) {
				setSeconds(Math.floor((Date.now() - recording.current.startedAt) / 1000));
			}
		}, 250);

		return () => clearInterval(timer);
	}, [phase]);

	useEffect(() => () => stop(), [session]);

	return {
		phase,
		seconds,
		error,
		stop,
		discard,
		start: () =>
			start().catch(() => {
				setPhase(null);
				setError(true);
				stop();
			}),
	};
}
